import React, { useState, useEffect, useContext, useMemo } from "react";
import "./styles.scss";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { GetAllLogs } from "../../../Services/ApiCalls";
import MainContext from "../../../Contexts/MainContext";
import EmptyData from "../../../Components/EmptyData";
import DataTable from "react-data-table-component";
import Lottie from "react-lottie";
import { FiFilter, FiEdit, FiCheckCircle, FiTrash2, FiMousePointer, FiChevronDown, FiChevronRight } from "react-icons/fi";

const LOADING = require("../../../Assets/animations/loading.json");

const Logs: React.FC = () => {
    const { currentLoggedUserId } = useContext(MainContext);

    const [searchValue, setSearchValue] = useState("");
    const [actionFilter, setActionFilter] = useState("TODOS");
    const [auditLevelFilter, setAuditLevelFilter] = useState("TODOS");
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    const { dataAllLogs, statusCodeAllLogs } = GetAllLogs(currentLoggedUserId);

    useEffect(() => {
        document.title = "Marca Ponto - Logs de Atividade";
    }, []);

    // Função para extrair Alvo e Nível de Auditoria
    const parseLogContent = (content: string) => {
        const lower = content.toLowerCase();
        let auditLevel = "OPERACIONAL";
        let target = "Sistema";
        let icon = <FiMousePointer color="#6B7280" size={18} />;
        let badgeClass = "badge-gray";

        // Determinando Nível e Ícone
        if (lower.includes("exclui") || lower.includes("remove") || lower.includes("deletou") || lower.includes("erro")) {
            auditLevel = "CRÍTICO";
            icon = <FiTrash2 color="#EF4444" size={18} />;
            badgeClass = "badge-red";
        } else if (lower.includes("edita") || lower.includes("altera") || lower.includes("atualizou")) {
            auditLevel = "CRÍTICO";
            icon = <FiEdit color="#F59E0B" size={18} />;
            badgeClass = "badge-amber";
        } else if (lower.includes("aprov") || lower.includes("crio") || lower.includes("adicionou")) {
            auditLevel = "OPERACIONAL";
            icon = <FiCheckCircle color="#10B981" size={18} />;
            badgeClass = "badge-green";
        }

        // Determinando o Alvo (Heurística simples)
        if (lower.includes("usuário") || lower.includes("colaborador") || lower.includes("gestor")) {
            const match = content.match(/(?:usuário|colaborador|gestor)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i);
            if (match && match[1]) target = match[1];
        } else if (lower.includes("ponto de")) {
            const match = content.match(/ponto de\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i);
            if (match && match[1]) target = match[1];
        } else if (lower.includes("jornada de") || lower.includes("expediente de")) {
            const match = content.match(/(?:jornada|expediente) de\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i);
            if (match && match[1]) target = match[1];
        } else if (lower.includes("setor") || lower.includes("função")) {
             target = "Estrutura Organizacional";
        }

        return { auditLevel, target, icon, badgeClass };
    };

    // Agrupamento e Filtragem
    const processedLogs = useMemo(() => {
        if (!dataAllLogs) return [];

        let filtered = dataAllLogs.filter((log: any) => {
            // 1. Text Search
            if (searchValue && !log.content.toLowerCase().includes(searchValue.toLowerCase())) return false;
            
            const parsed = parseLogContent(log.content);

            // 2. Action Filter (Reaproveitando a lógica de level/action)
            if (actionFilter !== "TODOS") {
                if (actionFilter === "CRÍTICO" && parsed.auditLevel !== "CRÍTICO") return false;
                if (actionFilter === "OPERACIONAL" && parsed.auditLevel !== "OPERACIONAL") return false;
            }

            // 3. Audit Level Filter
            if (auditLevelFilter !== "TODOS" && parsed.auditLevel !== auditLevelFilter) return false;

            return true;
        });

        // Date Filter robust parsing
        if (startDate || endDate) {
            filtered = filtered.filter((log: any) => {
                const parts = log.date.split(/[\s,]+/);
                const datePart = parts[0]; 
                const dParts = datePart.split("/");
                if(dParts.length === 3) {
                    const logDate = new Date(Number(dParts[2]), Number(dParts[1]) - 1, Number(dParts[0]));
                    if (startDate && logDate < startDate) return false;
                    if (endDate && logDate > endDate) return false;
                }
                return true;
            });
        }

        // Agrupamento de logs idênticos no mesmo dia
        const grouped: any[] = [];
        const map = new Map();

        filtered.forEach((log: any) => {
            const datePart = log.date.split(/[\s,]+/)[0];
            const parsed = parseLogContent(log.content);
            const key = `${datePart}-${log.content}`;
            
            if (map.has(key)) {
                const existing = map.get(key);
                existing.count += 1;
                existing.occurrences.push({ date: log.date });
                existing.date = log.date; 
            } else {
                const newLog = { 
                    ...log, 
                    count: 1, 
                    categoryData: parsed,
                    occurrences: [{ date: log.date }]
                };
                map.set(key, newLog);
                grouped.push(newLog);
            }
        });

        return grouped.reverse();
    }, [dataAllLogs, searchValue, actionFilter, auditLevelFilter, startDate, endDate]);

    const customColumns = [
        {
            name: "Ação",
            width: "60px",
            center: true,
            cell: (row: any) => row.categoryData.icon
        },
        {
            name: "Atividade",
            selector: (row: any) => row.content,
            sortable: true,
            grow: 2,
            cell: (row: any) => (
                <div className="log-content-cell">
                    <span className="log-text">{row.content}</span>
                    {row.count > 1 && (
                        <span className="log-badge-count">
                            {row.count} vezes
                        </span>
                    )}
                </div>
            )
        },
        {
            name: "Alvo da Ação",
            selector: (row: any) => row.categoryData.target,
            sortable: true,
            cell: (row: any) => <span className="log-target">{row.categoryData.target}</span>
        },
        {
            name: "Nível",
            selector: (row: any) => row.categoryData.auditLevel,
            sortable: true,
            center: true,
            cell: (row: any) => (
                <span className={`log-badge ${row.categoryData.badgeClass}`}>
                    {row.categoryData.auditLevel}
                </span>
            )
        },
        {
            name: "Última Ocorrência",
            selector: (row: any) => row.date,
            sortable: true,
            right: true,
            width: "180px",
            cell: (row: any) => <span className="log-date">{row.date}</span>
        }
    ];

    // Componente expansível para exibir os horários individuais
    const ExpandedComponent = ({ data }: any) => {
        if (data.count <= 1) return null; // Não expande se for só 1

        return (
            <div className="expanded-log-container">
                <h4>Ocorrências deste evento no dia</h4>
                <ul>
                    {data.occurrences.map((occ: any, i: number) => (
                        <li key={i}>
                            <FiCheckCircle size={14} color="#10B981" />
                            <span>{occ.date}</span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    return (
        <div className="logs__wrapper">
            <div className="logs__header">
                <div className="logs__title-info">
                    <h2 className="tt-title title-blue title-bold">Auditoria e Logs</h2>
                </div>
            </div>

            <div className="logs__filters">
                <div className="filter-group filter-search">
                    <label>Buscar</label>
                    <input
                        type="text"
                        placeholder="Pesquisar por termo ou usuário..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                    />
                </div>
                
                <div className="filter-group filter-action">
                    <label>Nível de Auditoria</label>
                    <select value={auditLevelFilter} onChange={(e) => setAuditLevelFilter(e.target.value)}>
                        <option value="TODOS">Todos os Níveis</option>
                        <option value="CRÍTICO">Crítico (Edições/Exclusões)</option>
                        <option value="OPERACIONAL">Operacional (Acessos/Criações)</option>
                    </select>
                </div>

                <div className="filter-group filter-date">
                    <label>Período</label>
                    <div className="date-pickers">
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date as Date)}
                            selectsStart
                            startDate={startDate}
                            endDate={endDate}
                            placeholderText="Data Início"
                            dateFormat="dd/MM/yyyy"
                            className="dp-input"
                        />
                        <span>-</span>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date as Date)}
                            selectsEnd
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startDate || undefined}
                            placeholderText="Data Fim"
                            dateFormat="dd/MM/yyyy"
                            className="dp-input"
                        />
                    </div>
                </div>
            </div>

            <div className="table__wrapper logs-table-wrapper">
                {statusCodeAllLogs === 200 ? (
                    <DataTable
                        noHeader={true}
                        data={processedLogs}
                        columns={customColumns as any}
                        noDataComponent={<EmptyData hasMargin={true} />}
                        striped={true}
                        pagination={true}
                        highlightOnHover={true}
                        paginationPerPage={30}
                        expandableRows
                        expandableRowsComponent={ExpandedComponent as any}
                        expandableRowExpanded={(row: any) => false}
                        expandableRowDisabled={(row: any) => row.count <= 1}
                    />
                ) : (
                    <div className="logs-loading">
                        <Lottie
                            options={{ loop: true, animationData: LOADING }}
                            height={120}
                            width={120}
                        />
                        <p>Carregando trilha de auditoria...</p>
                    </div>
                )}
            </div>
        </div>
    );
};
export default Logs;
